/**
 * 文档上传组件
 *
 * 支持文件选择和拖拽上传，用于上传文档到素材库
 * - 支持 .txt, .md, .docx 文件格式
 * - 支持批量上传
 * - 显示上传进度和状态
 */

import React, { useState, useCallback, useRef } from 'react';
import { documentApi } from '../../services/documentApi';
import type { DocumentUploadResponse, MaterialType } from '../../types/document';

const ACCEPTED_FILE_TYPES = ['.txt', '.md', '.docx'];
const ACCEPTED_MIME_TYPES = ['text/plain', 'text/markdown', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
const MAX_FILE_SIZE = 20 * 1024 * 1024;

// 素材类型选项
const MATERIAL_TYPE_OPTIONS = [
  {
    value: 'novel' as MaterialType,
    label: '小说正文',
    description: '提取5维度叙事标签：冲突类型、关系动态、情感弧光、叙事功能、经典母题',
    icon: '📖'
  },
  {
    value: 'technique' as MaterialType,
    label: '写作技巧',
    description: '提取3维度技巧标签：技巧类型、适用场景、核心要点',
    icon: '✏️'
  },
  {
    value: 'other' as MaterialType,
    label: '其他资料',
    description: '提取5维度通用标签：内容主题、领域分类、信息类型、关键要素、应用场景',
    icon: '📚'
  }
];

const UploadIcon: React.FC = () => (
  <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
  </svg>
);

const FileIcon: React.FC = () => (
  <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
  </svg>
);

const CheckCircleIcon: React.FC = () => (
  <svg className="w-6 h-6 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const XCircleIcon: React.FC = () => (
  <svg className="w-6 h-6 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 14l2-2m0 0l2-2m-2 2l-2-2m2 2l2 2m7-2a9 9 0 11-18 0 9 9 0 0118 0z" />
  </svg>
);

const TrashIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
  </svg>
);

const RefreshIcon: React.FC = () => (
  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
  </svg>
);

interface UploadFile {
  file: File;
  id: string;
  progress: number;
  status: 'pending' | 'uploading' | 'success' | 'error';
  error?: string;
  response?: DocumentUploadResponse;
  materialType?: MaterialType;
}

export interface DocumentUploadProps {
  workspaceId: number;
  onUploadComplete?: (response: DocumentUploadResponse) => void;
  onUploadError?: (error: Error) => void;
  className?: string;
  autoUpload?: boolean;
}

export const DocumentUpload: React.FC<DocumentUploadProps> = ({
  workspaceId,
  onUploadComplete,
  onUploadError,
  className = '',
  autoUpload = true
}) => {
  const [uploadFiles, setUploadFiles] = useState<UploadFile[]>([]);
  const [isDragOver, setIsDragOver] = useState(false);
  const [selectedMaterialType, setSelectedMaterialType] = useState<MaterialType | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): string | null => {
    const extension = '.' + file.name.split('.').pop()?.toLowerCase();
    
    if (!ACCEPTED_FILE_TYPES.includes(extension)) {
      return `不支持的文件格式：${extension}，支持 .txt, .md, .docx`;
    }

    if (!ACCEPTED_MIME_TYPES.includes(file.type) && !ACCEPTED_FILE_TYPES.includes(extension)) {
      return `不支持的文件类型：${file.type}`;
    }

    if (file.size > MAX_FILE_SIZE) {
      return `文件大小超过限制（最大 20MB）`;
    }

    return null;
  }, []);

  const formatFileSize = useCallback((bytes: number): string => {
    if (bytes < 1024) return bytes + ' B';
    if (bytes < 1024 * 1024) return (bytes / 1024).toFixed(1) + ' KB';
    return (bytes / (1024 * 1024)).toFixed(1) + ' MB';
  }, []);

  const handleFiles = useCallback((files: FileList | File[]) => {
    const newFiles: UploadFile[] = [];
    const errors: string[] = [];

    Array.from(files).forEach(file => {
      const error = validateFile(file);
      if (error) {
        errors.push(`${file.name}: ${error}`);
      } else {
        newFiles.push({
          file,
          id: `${file.name}-${Date.now()}-${Math.random()}`,
          progress: 0,
          status: 'pending',
          materialType: selectedMaterialType ?? undefined
        });
      }
    });

    if (errors.length > 0) {
      console.error('[DocumentUpload] 文件验证失败:', errors);
    }

    if (newFiles.length > 0) {
      setUploadFiles(prev => [...prev, ...newFiles]);
    }
  }, [validateFile, selectedMaterialType]);

  const uploadFile = useCallback(async (uploadFile: UploadFile) => {
    setUploadFiles(prev => prev.map(f => 
      f.id === uploadFile.id ? { ...f, status: 'uploading', progress: 10 } : f
    ));

    try {
      const progressInterval = setInterval(() => {
        setUploadFiles(prev => prev.map(f => {
          if (f.id === uploadFile.id && f.status === 'uploading' && f.progress < 90) {
            return { ...f, progress: f.progress + Math.random() * 20 };
          }
          return f;
        }));
      }, 200);

      const response = await documentApi.uploadDocument(undefined, {
        workspace_id: workspaceId,
        file: uploadFile.file,
        materialType: uploadFile.materialType
      });

      clearInterval(progressInterval);

      setUploadFiles(prev => prev.map(f => 
        f.id === uploadFile.id 
          ? { ...f, status: 'success', progress: 100, response } 
          : f
      ));

      onUploadComplete?.(response);
    } catch (error) {
      console.error('[DocumentUpload] 上传失败:', error);
      setUploadFiles(prev => prev.map(f => 
        f.id === uploadFile.id 
          ? { ...f, status: 'error', error: error instanceof Error ? error.message : '上传失败' } 
          : f
      ));
      onUploadError?.(error instanceof Error ? error : new Error('上传失败'));
    }
  }, [workspaceId, onUploadComplete, onUploadError]);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(true);
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragOver(false);
    
    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
  }, [handleFiles]);

  const handleFileSelect = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFiles(files);
    }
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, [handleFiles]);

  const handleDropZoneClick = useCallback(() => {
    fileInputRef.current?.click();
  }, []);

  const removeFile = useCallback((fileId: string) => {
    setUploadFiles(prev => prev.filter(f => f.id !== fileId));
  }, []);

  const retryUpload = useCallback((fileId: string) => {
    setUploadFiles(prev => {
      const file = prev.find(f => f.id === fileId);
      if (file) {
        uploadFile({ ...file, status: 'pending', progress: 0, error: undefined });
      }
      return prev;
    });
  }, [uploadFile]);

  React.useEffect(() => {
    if (autoUpload && selectedMaterialType) {
      uploadFiles.forEach(file => {
        if (file.status === 'pending' && file.materialType === selectedMaterialType) {
          uploadFile(file);
        }
      });
    }
  }, [uploadFiles, autoUpload, selectedMaterialType, uploadFile]);

  const handleUploadAll = useCallback(() => {
    uploadFiles.forEach(file => {
      if (file.status === 'pending' || file.status === 'error') {
        uploadFile({ ...file, status: 'pending', progress: 0, error: undefined });
      }
    });
  }, [uploadFiles, uploadFile]);

  const handleClearAll = useCallback(() => {
    setUploadFiles([]);
  }, []);

  return (
    <div className={`bg-white ${className}`}>
      {/* 素材类型选择 */}
      <div className="mb-6">
        <label className="block text-sm font-medium text-gray-700 mb-3">
          选择素材类型 <span className="text-red-500">*</span>
        </label>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          {MATERIAL_TYPE_OPTIONS.map((option) => (
            <button
              key={option.value}
              type="button"
              onClick={() => setSelectedMaterialType(option.value)}
              className={`
                relative p-4 rounded-lg border-2 text-left transition-all duration-150
                ${selectedMaterialType === option.value
                  ? 'border-paper-500 bg-paper-50 ring-2 ring-paper-200'
                  : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                }
              `}
            >
              <div className="flex items-start gap-3">
                <span className="text-2xl">{option.icon}</span>
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">{option.label}</div>
                  <div className="text-xs text-gray-500 mt-1">{option.description}</div>
                </div>
                {selectedMaterialType === option.value && (
                  <div className="absolute top-3 right-3">
                    <svg className="w-5 h-5 text-paper-600" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                    </svg>
                  </div>
                )}
              </div>
            </button>
          ))}
        </div>
        {!selectedMaterialType && (
          <p className="mt-2 text-sm text-amber-600">请先选择素材类型，然后上传文件</p>
        )}
      </div>

      <div
        onClick={handleDropZoneClick}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        className={`
          border-2 border-dashed rounded-lg p-8 text-center cursor-pointer transition-all duration-150
          ${isDragOver 
            ? 'border-paper-500 bg-paper-50' 
            : 'border-gray-300 hover:border-paper-400 hover:bg-gray-50'
          }
        `}
        role="button"
        tabIndex={0}
        aria-label="点击或拖拽文件到此处上传"
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') {
            handleDropZoneClick();
          }
        }}
      >
        <UploadIcon />
        <p className="mt-4 text-sm text-gray-600">
          <span className="font-medium text-paper-700">点击选择文件</span> 或拖拽到此处
        </p>
        <p className="mt-2 text-xs text-gray-500">
          支持 .txt, .md, .docx 格式，最大 20MB
        </p>
        <input
          ref={fileInputRef}
          type="file"
          onChange={handleFileSelect}
          accept={ACCEPTED_FILE_TYPES.join(',')}
          multiple
          className="hidden"
          aria-hidden="true"
        />
      </div>

      {uploadFiles.length > 0 && (
        <div className="mt-6">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-sm font-medium text-gray-700">
              上传列表 ({uploadFiles.length} 个文件)
            </h3>
            <div className="flex gap-2">
              <button
                onClick={handleUploadAll}
                className="px-3 py-1.5 text-xs font-medium text-paper-700 hover:bg-paper-50 rounded-md transition-colors"
              >
                全部上传
              </button>
              <button
                onClick={handleClearAll}
                className="px-3 py-1.5 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-md transition-colors"
              >
                清空列表
              </button>
            </div>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {uploadFiles.map(file => (
              <div
                key={file.id}
                className="flex items-center gap-3 p-3 bg-gray-50 rounded-md border border-gray-200"
              >
                <FileIcon />
                
                <div className="flex-1 min-w-0">
                  <div className="flex items-center justify-between">
                    <p className="text-sm font-medium text-gray-900 truncate" title={file.file.name}>
                      {file.file.name}
                    </p>
                    <div className="flex items-center gap-2">
                      {file.status === 'success' && <CheckCircleIcon />}
                      {file.status === 'error' && <XCircleIcon />}
                      {file.status === 'uploading' && (
                        <span className="text-xs text-gray-500">{Math.round(file.progress)}%</span>
                      )}
                      {(file.status === 'error' || file.status === 'pending') && (
                        <>
                          <button
                            onClick={() => retryUpload(file.id)}
                            className="p-1 hover:bg-gray-200 rounded transition-colors"
                            title="重新上传"
                            aria-label="重新上传"
                          >
                            <RefreshIcon />
                          </button>
                          <button
                            onClick={() => removeFile(file.id)}
                            className="p-1 hover:bg-red-100 text-red-600 rounded transition-colors"
                            title="移除"
                            aria-label="移除文件"
                          >
                            <TrashIcon />
                          </button>
                        </>
                      )}
                      {file.status === 'success' && (
                        <button
                          onClick={() => removeFile(file.id)}
                          className="p-1 hover:bg-red-100 text-red-600 rounded transition-colors"
                          title="移除"
                          aria-label="移除文件"
                        >
                          <TrashIcon />
                        </button>
                      )}
                    </div>
                  </div>
                  
                  <div className="flex items-center justify-between mt-1">
                    <p className="text-xs text-gray-500">
                      {formatFileSize(file.file.size)}
                    </p>
                    {file.status === 'uploading' && (
                      <div className="flex-1 mx-3">
                        <div className="h-1.5 bg-gray-200 rounded-full overflow-hidden">
                          <div
                            className="h-full bg-ink-800 transition-all duration-150"
                            style={{ width: `${file.progress}%` }}
                          />
                        </div>
                      </div>
                    )}
                    {file.status === 'success' && (
                      <span className="text-xs text-green-600">上传成功</span>
                    )}
                    {file.status === 'error' && (
                      <span className="text-xs text-red-600" title={file.error}>
                        {file.error || '上传失败'}
                      </span>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUpload;
